/* eslint-disable jsx-a11y/label-has-associated-control, jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */
import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { addEvent, updateEvent, getEvents } from '../slice/eventSlice';
import { FaTimes, FaUpload } from 'react-icons/fa';
import { toast } from 'react-toastify';
import axiosInstance from '../apis/axiosConfig';
import './AddEvent.css';
import { validateImageFile } from '../utils/fileValidation';

const AddEvent = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const isEditMode = !!id;
  const { data: events = [] } = useSelector((state) => state.events);
  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState({
    title: '',
    short_description: '',
    description: '',
    thumb: '',
    tags: '',
    seo_url: '',
    meta_title: '',
    meta_description: '',
    meta_keywords: '',
    status: 1,
    sort: 0
  });

  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (events.length === 0) {
      dispatch(getEvents());
    }
  }, [dispatch, events.length]);

  useEffect(() => {
    if (isEditMode && events.length > 0) {
      const ev = events.find((item) => item.id === parseInt(id));
      if (ev) {
        setFormData({
          title: ev.title || '',
          short_description: ev.short_description || '',
          description: ev.description || '',
          thumb: ev.thumb || '',
          tags: ev.tags || '',
          seo_url: ev.seo_url || '',
          meta_title: ev.meta_title || '',
          meta_description: ev.meta_description || '',
          meta_keywords: ev.meta_keywords || '',
          status: ev.status !== undefined ? ev.status : 1,
          sort: ev.sort !== undefined ? ev.sort : 0
        });
      }
    }
  }, [id, events, isEditMode]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    let updatedData = { ...formData, [name]: value };
    if (name === 'title') {
      updatedData.meta_title = value;
      updatedData.seo_url = value
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)/g, '');
    }
    if (name === 'short_description') {
      updatedData.meta_description = value;
    }

    setFormData(updatedData);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!validateImageFile(file, e)) return;
    if (!file) return;

    const uploadData = new FormData();
    uploadData.append('image', file);
    setUploading(true);

    try {
      const res = await axiosInstance.post('/products/upload', uploadData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const filename = res.data.filename;
      setFormData({ ...formData, thumb: filename });
      toast.success('Thumbnail uploaded successfully!');
    } catch (error) {
      console.error('Upload failed', error);
      toast.error('Image upload failed');
    } finally {
      setUploading(false);
    }
  };

  const getImageUrl = (image) => {
    if (!image) return null;
    const backendHost = process.env.REACT_APP_BACKEND
      ? process.env.REACT_APP_BACKEND.replace(/\/$/, '')
      : (process.env.REACT_APP_BACKEND_URL || '').split('/api')[0] || 'http://localhost:8000';
    return `${backendHost}/uploads/${image}`;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!formData.title || !formData.short_description || !formData.thumb) {
      toast.error('Please fill all required fields (*) and upload a thumbnail');
      return;
    }

    if (formData.short_description.length < 100) {
      toast.error('Short Description must be at least 100 characters');
      return;
    }

    if (formData.short_description.length > 750) {
      toast.error('Short Description cannot exceed 750 characters');
      return;
    }

    if (isEditMode) {
      dispatch(updateEvent({ id: parseInt(id), data: formData }));
    } else {
      dispatch(addEvent(formData));
    }

    navigate('/event-list');
  };

  return (
    <div className="add-event-container animate-fade-up">
      <div className="add-event-card">
        {/* Header */}
        <div className="add-event-header">
          <h2>{isEditMode ? 'Modify Event' : 'Create Event'}</h2>
          <button className="close-btn" onClick={() => navigate('/event-list')}>
            <FaTimes />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="premium-form-full">
          <div className="form-group">
            <label>
              Event Title <span className="required">*</span>
            </label>
            <input type="text" name="title" value={formData.title} onChange={handleInputChange} placeholder="Event Title" required />
            <small className="field-subtext">The Event Title will be used as the Meta Title</small>
          </div>

          <div className="form-group">
            <label>
              Short Description <span className="required">*</span>
            </label>
            <textarea
              name="short_description"
              value={formData.short_description}
              onChange={handleInputChange}
              placeholder="Short Description"
              rows="5"
              required
            />
            <div className="field-subtext-row">
              <small className="field-subtext">The Event Short Description will be used as the Meta Description</small>
              <small className="char-counter">{formData.short_description.length} / 750 Chars</small>
            </div>
            <small className="field-subtext note">Note: Min length: 100 Characters | Max length: 750 Characters</small>
          </div>

          <div className="form-group">
            <label>
              Event Thumb Image <span className="required">*</span>
            </label>
            <div className="file-uploader-box">
              <button type="button" className="btn-file-select" onClick={() => fileInputRef.current?.click()}>
                <FaUpload style={{ marginRight: '8px' }} /> Choose file
              </button>
              <span className="file-chosen-text">{formData.thumb ? formData.thumb : 'No file chosen'}</span>
              <input type="file" accept="image/*" style={{ display: 'none' }} ref={fileInputRef} onChange={handleImageUpload} />
            </div>
            {uploading && <small style={{ color: '#c4a882' }}>Uploading image...</small>}
            {formData.thumb && (
              <div className="thumb-preview-box">
                <img src={getImageUrl(formData.thumb)} alt="Thumb Preview" />
              </div>
            )}
          </div>

          <div className="form-actions">
            <button type="button" className="btn-premium-outline" onClick={() => navigate('/event-list')}>
              Cancel
            </button>
            <button type="submit" className="btn-premium">
              Submit
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddEvent;
